/**
 * Christian Liturgical Calendar Utility
 * Calculates dates for Christian seasons, celebrations, and provides themed content
 * Covers the full liturgical year with 25+ events
 */

// Calculate Easter Sunday using the Anonymous Gregorian algorithm
const calculateEaster = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

// Get date offset from a base date
const getDateOffset = (baseDate, days) => {
  const date = new Date(baseDate);
  date.setDate(date.getDate() + days);
  return date;
};

// Check if a date is within a range
const isDateInRange = (date, start, end) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const e = new Date(end);
  e.setHours(0, 0, 0, 0);
  return d >= s && d <= e;
};

// Check if dates are the same day
const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

// Christian Seasons Configuration
export const CHRISTIAN_SEASONS = {
  CHRISTMAS: {
    id: 'christmas',
    name: 'Christmas',
    subtitle: 'The Birth of Our Savior',
    message: 'For unto us a child is born, unto us a son is given. - Isaiah 9:6',
    colors: {
      primary: 'from-red-600 to-green-600',
      accent: 'gold',
      bg: 'from-red-900 via-green-900 to-red-900'
    },
    icon: '⭐',
    imageKeywords: 'nativity scene christmas manger bethlehem star'
  },
  ADVENT: {
    id: 'advent',
    name: 'Advent Season',
    subtitle: 'Preparing for the Coming of Christ',
    message: 'The people walking in darkness have seen a great light. - Isaiah 9:2',
    colors: {
      primary: 'from-purple-600 to-indigo-600',
      accent: 'purple',
      bg: 'from-purple-900 via-indigo-900 to-purple-900'
    },
    icon: '🕯️',
    imageKeywords: 'advent candles wreath purple christmas preparation'
  },
  EPIPHANY: {
    id: 'epiphany',
    name: 'Epiphany',
    subtitle: 'The Manifestation of Christ to the Gentiles',
    message: 'We saw his star when it rose and have come to worship him. - Matthew 2:2',
    colors: {
      primary: 'from-yellow-500 to-amber-600',
      accent: 'gold',
      bg: 'from-indigo-900 via-purple-900 to-indigo-900'
    },
    icon: '👑',
    imageKeywords: 'three wise men magi star bethlehem gifts gold'
  },
  ASH_WEDNESDAY: {
    id: 'ash_wednesday',
    name: 'Ash Wednesday',
    subtitle: 'Beginning of Lent',
    message: 'Remember that you are dust, and to dust you shall return. - Genesis 3:19',
    colors: {
      primary: 'from-gray-700 to-purple-800',
      accent: 'gray',
      bg: 'from-gray-900 via-purple-950 to-gray-900'
    },
    icon: '✝️',
    imageKeywords: 'ash cross forehead lent beginning repentance'
  },
  LENT: {
    id: 'lent',
    name: 'Lenten Season',
    subtitle: '40 Days of Reflection and Repentance',
    message: 'Create in me a clean heart, O God, and renew a right spirit within me. - Psalm 51:10',
    colors: {
      primary: 'from-purple-700 to-violet-800',
      accent: 'purple',
      bg: 'from-purple-950 via-violet-900 to-purple-950'
    },
    icon: '✝️',
    imageKeywords: 'cross desert prayer fasting lent purple cloth'
  },
  PALM_SUNDAY: {
    id: 'palm_sunday',
    name: 'Palm Sunday',
    subtitle: 'The Triumphal Entry into Jerusalem',
    message: 'Hosanna! Blessed is he who comes in the name of the Lord! - Mark 11:9',
    colors: {
      primary: 'from-green-600 to-emerald-700',
      accent: 'green',
      bg: 'from-green-900 via-emerald-900 to-green-900'
    },
    icon: '🌿',
    imageKeywords: 'palm branches jerusalem triumphal entry donkey crowd'
  },
  HOLY_WEEK: {
    id: 'holy_week',
    name: 'Holy Week',
    subtitle: 'Walking with Christ to Calvary',
    message: 'He was despised and rejected by mankind, a man of suffering. - Isaiah 53:3',
    colors: {
      primary: 'from-purple-800 to-red-900',
      accent: 'purple',
      bg: 'from-purple-950 via-red-950 to-purple-950'
    },
    icon: '⛪',
    imageKeywords: 'holy week passion cross jerusalem gethsemane'
  },
  MAUNDY_THURSDAY: {
    id: 'maundy_thursday',
    name: 'Maundy Thursday',
    subtitle: 'The Last Supper',
    message: 'A new commandment I give unto you, That ye love one another. - John 13:34',
    colors: {
      primary: 'from-purple-700 to-red-800',
      accent: 'white',
      bg: 'from-purple-950 via-red-900 to-purple-950'
    },
    icon: '🍷',
    imageKeywords: 'last supper communion bread wine disciples'
  },
  GOOD_FRIDAY: {
    id: 'good_friday',
    name: 'Good Friday',
    subtitle: 'The Crucifixion of Our Lord',
    message: 'For God so loved the world that he gave his one and only Son. - John 3:16',
    colors: {
      primary: 'from-gray-800 to-black',
      accent: 'red',
      bg: 'from-gray-900 via-black to-gray-900'
    },
    icon: '✝️',
    imageKeywords: 'crucifixion cross calvary golgotha redemption'
  },
  EASTER: {
    id: 'easter',
    name: 'Easter Sunday',
    subtitle: 'He Is Risen!',
    message: 'He is not here; he has risen! - Luke 24:6',
    colors: {
      primary: 'from-yellow-400 to-amber-500',
      accent: 'white',
      bg: 'from-amber-600 via-yellow-500 to-amber-600'
    },
    icon: '🌅',
    imageKeywords: 'resurrection empty tomb risen christ easter sunrise'
  },
  EASTER_MONDAY: {
    id: 'easter_monday',
    name: 'Easter Monday',
    subtitle: 'Continuing the Easter Joy',
    message: 'Death has been swallowed up in victory. - 1 Corinthians 15:54',
    colors: {
      primary: 'from-yellow-400 to-amber-500',
      accent: 'white',
      bg: 'from-amber-600 via-yellow-500 to-amber-600'
    },
    icon: '🌅',
    imageKeywords: 'easter celebration joy risen victory'
  },
  ASCENSION: {
    id: 'ascension',
    name: 'Ascension Day',
    subtitle: 'Christ Ascends to Heaven',
    message: 'He was taken up before their very eyes, and a cloud hid him from their sight. - Acts 1:9',
    colors: {
      primary: 'from-sky-500 to-blue-600',
      accent: 'white',
      bg: 'from-sky-700 via-blue-800 to-sky-700'
    },
    icon: '☁️',
    imageKeywords: 'ascension heaven clouds jesus risen glory'
  },
  PENTECOST: {
    id: 'pentecost',
    name: 'Pentecost',
    subtitle: 'The Coming of the Holy Spirit',
    message: 'They were all filled with the Holy Spirit. - Acts 2:4',
    colors: {
      primary: 'from-red-500 to-orange-600',
      accent: 'red',
      bg: 'from-red-800 via-orange-700 to-red-800'
    },
    icon: '🔥',
    imageKeywords: 'pentecost fire holy spirit flames dove tongues'
  },
  TRINITY_SUNDAY: {
    id: 'trinity_sunday',
    name: 'Trinity Sunday',
    subtitle: 'Father, Son, and Holy Spirit',
    message: 'The grace of the Lord Jesus Christ, the love of God, and the fellowship of the Holy Spirit be with you all. - 2 Corinthians 13:14',
    colors: {
      primary: 'from-blue-600 to-indigo-700',
      accent: 'gold',
      bg: 'from-blue-900 via-indigo-900 to-blue-900'
    },
    icon: '🕊️',
    imageKeywords: 'trinity holy spirit dove three-in-one'
  },
  CORPUS_CHRISTI: {
    id: 'corpus_christi',
    name: 'Corpus Christi',
    subtitle: 'The Body and Blood of Christ',
    message: 'This is my body, which is given for you. Do this in remembrance of me. - Luke 22:19',
    colors: {
      primary: 'from-amber-600 to-yellow-600',
      accent: 'gold',
      bg: 'from-amber-900 via-yellow-800 to-amber-900'
    },
    icon: '🍞',
    imageKeywords: 'communion eucharist bread wine chalice'
  },
  TRANSFIGURATION: {
    id: 'transfiguration',
    name: 'Transfiguration',
    subtitle: 'The Glory of Christ Revealed',
    message: 'His face shone like the sun, and his clothes became as white as the light. - Matthew 17:2',
    colors: {
      primary: 'from-yellow-300 to-white',
      accent: 'gold',
      bg: 'from-sky-700 via-amber-600 to-sky-700'
    },
    icon: '✨',
    imageKeywords: 'transfiguration mountain glory light moses elijah'
  },
  ASSUMPTION: {
    id: 'assumption',
    name: 'Assumption of Mary',
    subtitle: 'Mary Taken Up to Heaven',
    message: 'My soul magnifies the Lord, and my spirit rejoices in God my Savior. - Luke 1:46-47',
    colors: {
      primary: 'from-blue-500 to-sky-600',
      accent: 'white',
      bg: 'from-blue-900 via-sky-800 to-blue-900'
    },
    icon: '🌹',
    imageKeywords: 'mary assumption heaven roses blessed mother'
  },
  HARVEST_FESTIVAL: {
    id: 'harvest_festival',
    name: 'Harvest Festival',
    subtitle: 'Thanksgiving for God\'s Provision',
    message: 'The earth is the Lord\'s, and everything in it. - Psalm 24:1',
    colors: {
      primary: 'from-amber-600 to-orange-600',
      accent: 'gold',
      bg: 'from-amber-900 via-orange-800 to-amber-900'
    },
    icon: '🌾',
    imageKeywords: 'harvest wheat thanksgiving fruit abundance autumn'
  },
  ALL_SAINTS: {
    id: 'all_saints',
    name: 'All Saints Day',
    subtitle: 'Honoring the Faithful',
    message: 'These are they who have come out of the great tribulation. - Revelation 7:14',
    colors: {
      primary: 'from-amber-500 to-yellow-600',
      accent: 'gold',
      bg: 'from-slate-800 via-amber-900 to-slate-800'
    },
    icon: '🕯️',
    imageKeywords: 'saints candles memorial heaven faithful'
  },
  ALL_SOULS: {
    id: 'all_souls',
    name: 'All Souls Day',
    subtitle: 'Remembering the Faithful Departed',
    message: 'Blessed are the dead who die in the Lord. - Revelation 14:13',
    colors: {
      primary: 'from-purple-700 to-gray-700',
      accent: 'purple',
      bg: 'from-gray-900 via-purple-900 to-gray-900'
    },
    icon: '🕯️',
    imageKeywords: 'remembrance candles memorial departed souls'
  },
  WORLD_PRAYER_DAY: {
    id: 'world_prayer_day',
    name: 'World Day of Prayer',
    subtitle: 'United in Prayer Across Nations',
    message: 'If my people, who are called by my name, will humble themselves and pray. - 2 Chronicles 7:14',
    colors: {
      primary: 'from-teal-600 to-emerald-700',
      accent: 'white',
      bg: 'from-teal-900 via-emerald-800 to-teal-900'
    },
    icon: '🙏',
    imageKeywords: 'prayer hands together global unity church'
  },
  ORDINARY: {
    id: 'ordinary',
    name: 'Ordinary Time',
    subtitle: 'Growing in Faith',
    message: 'The Lord is my shepherd; I shall not want. - Psalm 23:1',
    colors: {
      primary: 'from-green-500 to-teal-600',
      accent: 'green',
      bg: 'from-slate-800 via-slate-900 to-slate-800'
    },
    icon: '🌿',
    imageKeywords: 'church worship prayer congregation faith'
  }
};

// Get the current Christian season
export const getCurrentSeason = (date = new Date()) => {
  const year = date.getFullYear();
  const easter = calculateEaster(year);
  
  // Calculate key dates
  const christmas = new Date(year, 11, 25); // Dec 25
  const christmasEnd = new Date(year, 11, 31); // Dec 31
  const christmasStart = new Date(year, 11, 24); // Dec 24 (Christmas Eve)
  
  // Advent: 4 Sundays before Christmas (roughly Nov 27 - Dec 23)
  const adventStart = new Date(year, 10, 27); // Approximate
  const adventEnd = new Date(year, 11, 23);
  
  // Epiphany: Jan 6
  const epiphany = new Date(year, 0, 6);
  const epiphanyEnd = new Date(year, 0, 12);
  
  // Ash Wednesday: 46 days before Easter
  const ashWednesday = getDateOffset(easter, -46);
  
  // Lent: Day after Ash Wednesday to day before Palm Sunday
  const lentStart = getDateOffset(easter, -45);
  const lentEnd = getDateOffset(easter, -8);
  
  // Palm Sunday: 7 days before Easter
  const palmSunday = getDateOffset(easter, -7);
  
  // Holy Week: Mon-Wed before Easter
  const holyWeekStart = getDateOffset(easter, -6);
  const holyWeekEnd = getDateOffset(easter, -4);
  
  // Maundy Thursday: 3 days before Easter
  const maundyThursday = getDateOffset(easter, -3);
  
  // Good Friday: 2 days before Easter
  const goodFriday = getDateOffset(easter, -2);
  
  // Easter Monday: day after Easter
  const easterMonday = getDateOffset(easter, 1);
  
  // Easter Season: Easter Sunday + Octave
  const easterEnd = getDateOffset(easter, 7);
  
  // Ascension: 39 days after Easter
  const ascension = getDateOffset(easter, 39);
  const ascensionEnd = getDateOffset(easter, 42);
  
  // Pentecost: 49 days after Easter
  const pentecost = getDateOffset(easter, 49);
  const pentecostEnd = getDateOffset(easter, 50);
  
  // Trinity Sunday: 56 days after Easter
  const trinitySunday = getDateOffset(easter, 56);
  
  // Corpus Christi: 60 days after Easter
  const corpusChristi = getDateOffset(easter, 60);
  
  // Fixed-date celebrations
  const transfiguration = new Date(year, 7, 6); // Aug 6
  const assumption = new Date(year, 7, 15); // Aug 15
  const allSaints = new Date(year, 10, 1); // Nov 1
  const allSouls = new Date(year, 10, 2); // Nov 2
  
  // World Day of Prayer: first Friday of March
  const marchFirst = new Date(year, 2, 1);
  const worldPrayerDay = new Date(year, 2, 1 + ((5 - marchFirst.getDay() + 7) % 7));
  
  // Harvest Festival: last Sunday of September
  const sepLast = new Date(year, 9, 0); // Last day of September
  const harvestFestival = new Date(year, 8, sepLast.getDate() - ((sepLast.getDay()) % 7));
  
  // Check current date against seasons (most specific first)
  
  // Christmas (Dec 24-31)
  if (isDateInRange(date, christmasStart, christmasEnd)) {
    return { ...CHRISTIAN_SEASONS.CHRISTMAS, year };
  }
  
  // Christmas (Jan 1-5 of next year celebration)
  if (date.getMonth() === 0 && date.getDate() <= 5) {
    return { ...CHRISTIAN_SEASONS.CHRISTMAS, year };
  }
  
  // Epiphany (Jan 6-12)
  if (isDateInRange(date, epiphany, epiphanyEnd)) {
    return { ...CHRISTIAN_SEASONS.EPIPHANY, year };
  }
  
  // Advent
  if (isDateInRange(date, adventStart, adventEnd)) {
    return { ...CHRISTIAN_SEASONS.ADVENT, year };
  }
  
  // All Saints Day (Nov 1)
  if (isSameDay(date, allSaints)) {
    return { ...CHRISTIAN_SEASONS.ALL_SAINTS, year };
  }
  
  // All Souls Day (Nov 2)
  if (isSameDay(date, allSouls)) {
    return { ...CHRISTIAN_SEASONS.ALL_SOULS, year };
  }
  
  // Harvest Festival (last Sunday of Sep)
  if (isSameDay(date, harvestFestival)) {
    return { ...CHRISTIAN_SEASONS.HARVEST_FESTIVAL, year };
  }
  
  // Transfiguration (Aug 6)
  if (isSameDay(date, transfiguration)) {
    return { ...CHRISTIAN_SEASONS.TRANSFIGURATION, year };
  }
  
  // Assumption (Aug 15)
  if (isSameDay(date, assumption)) {
    return { ...CHRISTIAN_SEASONS.ASSUMPTION, year };
  }
  
  // World Day of Prayer (first Friday of March)
  if (isSameDay(date, worldPrayerDay)) {
    return { ...CHRISTIAN_SEASONS.WORLD_PRAYER_DAY, year };
  }
  
  // Ash Wednesday (specific day)
  if (isSameDay(date, ashWednesday)) {
    return { ...CHRISTIAN_SEASONS.ASH_WEDNESDAY, year, easterDate: easter };
  }
  
  // Good Friday (specific day)
  if (isSameDay(date, goodFriday)) {
    return { ...CHRISTIAN_SEASONS.GOOD_FRIDAY, year, easterDate: easter };
  }
  
  // Maundy Thursday (specific day)
  if (isSameDay(date, maundyThursday)) {
    return { ...CHRISTIAN_SEASONS.MAUNDY_THURSDAY, year, easterDate: easter };
  }
  
  // Palm Sunday (specific day)
  if (isSameDay(date, palmSunday)) {
    return { ...CHRISTIAN_SEASONS.PALM_SUNDAY, year, easterDate: easter };
  }
  
  // Holy Week (Mon-Wed before Easter)
  if (isDateInRange(date, holyWeekStart, holyWeekEnd)) {
    return { ...CHRISTIAN_SEASONS.HOLY_WEEK, year, easterDate: easter };
  }
  
  // Easter Sunday and Octave
  if (isSameDay(date, easter)) {
    return { ...CHRISTIAN_SEASONS.EASTER, year, easterDate: easter };
  }
  
  // Easter Monday
  if (isSameDay(date, easterMonday)) {
    return { ...CHRISTIAN_SEASONS.EASTER_MONDAY, year, easterDate: easter };
  }
  
  // Easter Season (rest of octave)
  if (isDateInRange(date, getDateOffset(easter, 2), easterEnd)) {
    return { ...CHRISTIAN_SEASONS.EASTER, year, easterDate: easter };
  }
  
  // Lent (day after Ash Wednesday to day before Palm Sunday)
  if (isDateInRange(date, lentStart, lentEnd)) {
    return { ...CHRISTIAN_SEASONS.LENT, year, easterDate: easter };
  }
  
  // Ascension
  if (isDateInRange(date, ascension, ascensionEnd)) {
    return { ...CHRISTIAN_SEASONS.ASCENSION, year, easterDate: easter };
  }
  
  // Pentecost
  if (isDateInRange(date, pentecost, pentecostEnd)) {
    return { ...CHRISTIAN_SEASONS.PENTECOST, year, easterDate: easter };
  }
  
  // Trinity Sunday
  if (isSameDay(date, trinitySunday)) {
    return { ...CHRISTIAN_SEASONS.TRINITY_SUNDAY, year, easterDate: easter };
  }
  
  // Corpus Christi
  if (isSameDay(date, corpusChristi)) {
    return { ...CHRISTIAN_SEASONS.CORPUS_CHRISTI, year, easterDate: easter };
  }
  
  // Default: Ordinary Time
  return { ...CHRISTIAN_SEASONS.ORDINARY, year };
};

// Get ALL upcoming Christian events (comprehensive list)
export const getUpcomingEvents = (date = new Date(), count = 3) => {
  const year = date.getFullYear();
  const easter = calculateEaster(year);
  const nextEaster = calculateEaster(year + 1);
  
  // Fixed-date helpers
  const marchFirst = new Date(year, 2, 1);
  const worldPrayerDay = new Date(year, 2, 1 + ((5 - marchFirst.getDay() + 7) % 7));
  const sepLast = new Date(year, 9, 0);
  const harvestFestival = new Date(year, 8, sepLast.getDate() - ((sepLast.getDay()) % 7));
  
  // Next year helpers
  const nextMarchFirst = new Date(year + 1, 2, 1);
  const nextWorldPrayerDay = new Date(year + 1, 2, 1 + ((5 - nextMarchFirst.getDay() + 7) % 7));
  
  const events = [
    // Easter-based (current year)
    { name: 'World Day of Prayer', date: worldPrayerDay, season: CHRISTIAN_SEASONS.WORLD_PRAYER_DAY },
    { name: 'Ash Wednesday', date: getDateOffset(easter, -46), season: CHRISTIAN_SEASONS.ASH_WEDNESDAY },
    { name: 'Palm Sunday', date: getDateOffset(easter, -7), season: CHRISTIAN_SEASONS.PALM_SUNDAY },
    { name: 'Maundy Thursday', date: getDateOffset(easter, -3), season: CHRISTIAN_SEASONS.MAUNDY_THURSDAY },
    { name: 'Good Friday', date: getDateOffset(easter, -2), season: CHRISTIAN_SEASONS.GOOD_FRIDAY },
    { name: 'Easter Sunday', date: easter, season: CHRISTIAN_SEASONS.EASTER },
    { name: 'Easter Monday', date: getDateOffset(easter, 1), season: CHRISTIAN_SEASONS.EASTER_MONDAY },
    { name: 'Ascension Day', date: getDateOffset(easter, 39), season: CHRISTIAN_SEASONS.ASCENSION },
    { name: 'Pentecost', date: getDateOffset(easter, 49), season: CHRISTIAN_SEASONS.PENTECOST },
    { name: 'Trinity Sunday', date: getDateOffset(easter, 56), season: CHRISTIAN_SEASONS.TRINITY_SUNDAY },
    { name: 'Corpus Christi', date: getDateOffset(easter, 60), season: CHRISTIAN_SEASONS.CORPUS_CHRISTI },
    
    // Fixed-date (current year)
    { name: 'Transfiguration', date: new Date(year, 7, 6), season: CHRISTIAN_SEASONS.TRANSFIGURATION },
    { name: 'Assumption of Mary', date: new Date(year, 7, 15), season: CHRISTIAN_SEASONS.ASSUMPTION },
    { name: 'Harvest Festival', date: harvestFestival, season: CHRISTIAN_SEASONS.HARVEST_FESTIVAL },
    { name: 'All Saints Day', date: new Date(year, 10, 1), season: CHRISTIAN_SEASONS.ALL_SAINTS },
    { name: 'All Souls Day', date: new Date(year, 10, 2), season: CHRISTIAN_SEASONS.ALL_SOULS },
    { name: 'Advent Begins', date: new Date(year, 10, 27), season: CHRISTIAN_SEASONS.ADVENT },
    { name: 'Christmas', date: new Date(year, 11, 25), season: CHRISTIAN_SEASONS.CHRISTMAS },
    
    // Next year events (for end-of-year coverage)
    { name: 'Epiphany', date: new Date(year + 1, 0, 6), season: CHRISTIAN_SEASONS.EPIPHANY },
    { name: 'World Day of Prayer', date: nextWorldPrayerDay, season: CHRISTIAN_SEASONS.WORLD_PRAYER_DAY },
    { name: 'Ash Wednesday', date: getDateOffset(nextEaster, -46), season: CHRISTIAN_SEASONS.ASH_WEDNESDAY },
    { name: 'Palm Sunday', date: getDateOffset(nextEaster, -7), season: CHRISTIAN_SEASONS.PALM_SUNDAY },
    { name: 'Easter Sunday', date: nextEaster, season: CHRISTIAN_SEASONS.EASTER },
  ];
  
  // Filter future events and sort by date
  return events
    .filter(e => e.date > date)
    .sort((a, b) => a.date - b.date)
    .slice(0, count);
};

// Get days until an event
export const getDaysUntil = (targetDate, fromDate = new Date()) => {
  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);
  const from = new Date(fromDate);
  from.setHours(0, 0, 0, 0);
  return Math.ceil((target - from) / (1000 * 60 * 60 * 24));
};

// Season-specific images (curated URLs or placeholders)
export const SEASON_IMAGES = {
  christmas: 'https://images.unsplash.com/photo-1512389142860-9c449e58a814?w=1200&h=400&fit=crop',
  advent: 'https://images.unsplash.com/photo-1576919228236-a097c32a5cd4?w=1200&h=400&fit=crop',
  epiphany: 'https://images.unsplash.com/photo-1545048702-79362596cdc9?w=1200&h=400&fit=crop',
  lent: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&h=400&fit=crop',
  ash_wednesday: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&h=400&fit=crop',
  palm_sunday: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=1200&h=400&fit=crop',
  holy_week: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200&h=400&fit=crop',
  maundy_thursday: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200&h=400&fit=crop',
  good_friday: 'https://images.unsplash.com/photo-1515162816999-a0c47dc192f7?w=1200&h=400&fit=crop',
  easter: 'https://images.unsplash.com/photo-1457530378978-8bac673b8062?w=1200&h=400&fit=crop',
  easter_monday: 'https://images.unsplash.com/photo-1457530378978-8bac673b8062?w=1200&h=400&fit=crop',
  ascension: 'https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=1200&h=400&fit=crop',
  pentecost: 'https://images.unsplash.com/photo-1493836512294-502baa1986e2?w=1200&h=400&fit=crop',
  trinity_sunday: 'https://images.unsplash.com/photo-1493836512294-502baa1986e2?w=1200&h=400&fit=crop',
  corpus_christi: 'https://images.unsplash.com/photo-1493836512294-502baa1986e2?w=1200&h=400&fit=crop',
  transfiguration: 'https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=1200&h=400&fit=crop',
  assumption: 'https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=1200&h=400&fit=crop',
  harvest_festival: 'https://images.unsplash.com/photo-1508193638397-1c4234db14d8?w=1200&h=400&fit=crop',
  all_saints: 'https://images.unsplash.com/photo-1493836512294-502baa1986e2?w=1200&h=400&fit=crop',
  all_souls: 'https://images.unsplash.com/photo-1493836512294-502baa1986e2?w=1200&h=400&fit=crop',
  world_prayer_day: 'https://images.unsplash.com/photo-1438032005730-c779502df39b?w=1200&h=400&fit=crop',
  ordinary: 'https://images.unsplash.com/photo-1438032005730-c779502df39b?w=1200&h=400&fit=crop'
};

export default {
  getCurrentSeason,
  getUpcomingEvents,
  getDaysUntil,
  CHRISTIAN_SEASONS,
  SEASON_IMAGES
};
